import { FeaturedCharities } from '@components/featured-charities';
import { Question } from '@components/ui/question';
import { Wizard } from '@components/ui/wizard';
import {
  CollectionTypeEnum,
  Organization,
  ValidationError,
} from '@givz/core-client';
import { useData } from '@hooks/form';
import AttachMoneySharpIcon from '@mui/icons-material/AttachMoneySharp';
import { Box, Chip, InputAdornment, Stack, Typography } from '@mui/material';
import { useCuratedCollection } from '@queries/collections';
import { formatUsd } from '@utils/money';
import { FunctionComponent } from 'react';
import { config } from '@config';

export type IncentiveWizardState = {
  name: string;
  offer: number;
  featuredCharitiesType: CollectionTypeEnum;
  selectedCharities: Map<string, Organization>;
};

export type IncentiveWizardProps = {
  errors?: ValidationError[] | null;
  useData: ReturnType<typeof useData<IncentiveWizardState>>;
};

export const defaultIncentiveWizard = (): IncentiveWizardState => ({
  featuredCharitiesType: CollectionTypeEnum.Curated,
  name: '',
  offer: 0,
  selectedCharities: new Map(),
});

export const IncentiveWizard: FunctionComponent<IncentiveWizardProps> = (
  props,
) => {
  const { errors, useData } = props;
  const curatedCollectionQuery = useCuratedCollection();
  const curatedCollection = curatedCollectionQuery.data?.data;
  const { data, setValueFromKey } = useData;

  const onDoneFeaturedCharities = () => {
    if (data.featuredCharitiesType === CollectionTypeEnum.Curated) {
      setValueFromKey('selectedCharities')(
        new Map(
          curatedCollection?.organizations.map((organization) => [
            organization.gid,
            organization,
          ]),
        ),
      );
    }
  };

  const onChangeOffer = (value: number) => {
    setValueFromKey('offer')(Math.min(value, config.maxDonationValue));
  };

  const nameError = errors?.find(({ field }) => field === 'name');

  return (
    <Box sx={{ flex: '1 1 auto' }}>
      <Wizard
        steps={[
          {
            content: (
              <Question
                inputProps={{
                  error: Boolean(nameError),
                  helperText: nameError?.message,
                  id: 'incentive-name',
                  label: 'Incentive Name',
                  variant: 'outlined',
                }}
                onChange={setValueFromKey('name')}
                title="What's your incentive name:"
                value={data.name}
              />
            ),
            isEditable: () => data.name !== '',
            isValid: () => data.name.length > 2,
            labelContent: 'Incentive Name',
            labelProps: {
              optional: (
                <Typography color={nameError ? 'error' : ''}>
                  {data.name}
                </Typography>
              ),
            },
          },
          {
            content: (
              <Question
                inputProps={{
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneySharpIcon />
                      </InputAdornment>
                    ),
                  },
                  id: 'incentive-offer',
                  label: 'Incentive Offer',
                  type: 'number',

                  variant: 'outlined',
                }}
                onChange={onChangeOffer}
                title="What's your incentive offer:"
                value={data.offer}
              />
            ),
            isEditable: () => data.offer > 0,
            isValid: () => data.offer > 0,
            labelContent: 'Offer Settings',
            labelProps: {
              optional: formatUsd(data.offer),
            },
          },
          {
            content: (
              <FeaturedCharities
                type={data.featuredCharitiesType}
                onChangeType={setValueFromKey('featuredCharitiesType')}
                onChangeSelectedCharities={setValueFromKey('selectedCharities')}
                selectedCharities={data.selectedCharities}
              />
            ),
            isEditable: () => data.selectedCharities.size > 0,
            isValid: () =>
              data.selectedCharities.size > 2 ||
              data.featuredCharitiesType === CollectionTypeEnum.Curated,
            labelContent: 'Featured Charities',
            labelProps: {
              optional: (
                <Stack direction="row" flexWrap="wrap">
                  {Array.from(data.selectedCharities.entries()).map(
                    ([id, data]) => (
                      <Chip
                        key={id}
                        label={data.name}
                        sx={{ marginBottom: 1, marginRight: 1 }}
                      />
                    ),
                  )}
                </Stack>
              ),
            },
            onDone: onDoneFeaturedCharities,
          },
        ]}
      />
    </Box>
  );
};
